/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useEffect, useState } from "react";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// @mui material components
import Card from "@mui/material/Card";
import Pagination from "@mui/material/Pagination";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";


// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Data
import generateLeadsTableData from "layouts/leads/data/leadsTableData";
import { api } from "../../services/api"; // Importando o serviço de API

function Leads({ history }) {
  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalLeads, setTotalLeads] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterFlag, setFilterFlag] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        pageSize,
        search: searchTerm,
        flag: filterFlag,
      });
      const data = await api.get(`/leads?${params.toString()}`);
      setLeads(data.leads);
      setTotalLeads(data.total_leads);
    } catch (error) {
      console.error("Erro ao buscar leads:", error);
      if (error.message.includes("401")) {
        history.push("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceFetch = setTimeout(() => {
      fetchLeads();
    }, 500); // Adiciona um debounce de 500ms para a busca

    return () => clearTimeout(debounceFetch);
  }, [page, pageSize, searchTerm, filterFlag, history]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1); // Reset to first page when page size changes
  };

  const handleOpenDialog = (lead) => {
    setSelectedLead(lead);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedLead(null);
    setOpenDialog(false);
  };

  const handleConfirmFlag = async () => {
    if (selectedLead) {
      try {
        await api.post(`/leads/${selectedLead.id}/flag`);
        fetchLeads(); // Refresh the leads data
      } catch (error) {
        console.error("Erro ao marcar lead como fraudador:", error);
        // Adicionar notificação de erro para o usuário
      }
    }
    handleCloseDialog();
  };

  const { columns, rows } = generateLeadsTableData(leads, handleOpenDialog);

  const totalPages = Math.ceil(totalLeads / pageSize);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Card>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="22px">
              <VuiTypography variant="lg" color="white">
                Leads
              </VuiTypography>
              <VuiBox display="flex" alignItems="center">
                <VuiInput
                  placeholder="Buscar por nome, telefone..."
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    width: "240px",
                    marginRight: "16px",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.1) !important",
                      backdropFilter: "blur(12px) !important",
                      borderRadius: "15px !important",
                      border: `1px solid rgba(255, 255, 255, 0.4) !important`,
                    },
                  }}
                />
                <FormControl
                  variant="outlined"
                  sx={{
                    minWidth: 150,
                    "& .MuiInputBase-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.1) !important",
                      backdropFilter: "blur(12px) !important",
                      borderRadius: "15px !important",
                      border: `1px solid rgba(255, 255, 255, 0.4)`,
                      color: "white !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                    },
                    "& .MuiSelect-icon": {
                      color: "white !important",
                    },
                  }}
                >
                  <Select
                    value={filterFlag}
                    onChange={(e) => setFilterFlag(e.target.value)}
                    displayEmpty
                    IconComponent={KeyboardArrowDownIcon}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: "#1f2128",
                          "& .MuiMenuItem-root": {
                            color: "white",
                            "&.Mui-selected": {
                              backgroundColor: "rgba(0, 123, 255, 0.5) !important",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "rgba(0, 123, 255, 0.6) !important",
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Todas as Flags</em>
                    </MenuItem>
                    <MenuItem value="baixo risco">Baixo Risco</MenuItem>
                    <MenuItem value="suspeitos">Suspeitos</MenuItem>
                  </Select>
                </FormControl>
              </VuiBox>
            </VuiBox>
            <VuiBox
              sx={{
                minHeight: "60vh",
                "& th": {
                  borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                    `${borderWidth[1]} solid ${grey[700]}`,
                },
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                      `${borderWidth[1]} solid ${grey[700]}`,
                  },
                },
              }}
            >
              {loading ? (
                <VuiBox sx={{ pt: 3, pb: 2, pl: 2, pr: 2 }}>
                  <Skeleton height={40} count={pageSize} baseColor="#1f2128" highlightColor="#2a2c35" />
                </VuiBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </VuiBox>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mt={3}>
              <VuiBox display="flex" alignItems="center">
                <FormControl variant="outlined" sx={{ minWidth: 120 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    IconComponent={KeyboardArrowDownIcon} // Adiciona o ícone de seta para baixo
                    sx={{
                      "&.MuiInputBase-root": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(12px)",
                        borderRadius: "15px",
                        color: "white",
                      },
                      "& .MuiSelect-icon": {
                        color: "white",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.4)",
                        borderRadius: "15px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.6)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "white",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: "#1f2128",
                          "& .MuiMenuItem-root": {
                            color: "white",
                            "&.Mui-selected": {
                              backgroundColor: "rgba(0, 123, 255, 0.5) !important",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "rgba(0, 123, 255, 0.6) !important",
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <VuiTypography variant="button" color="white" sx={{ marginLeft: '10px' }}>
                  /página
                </VuiTypography>
              </VuiBox>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                variant="text" // Changed from outlined to text
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "white !important",
                    border: "none",
                  },
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: "info.main",
                    color: "white !important",
                    border: "none",
                  },
                  "& .MuiPaginationItem-root.Mui-selected:hover": {
                    backgroundColor: "info.main",
                  },
                  "& .MuiPaginationItem-root:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              />
            </VuiBox>
          </Card>
        </VuiBox>
      </VuiBox>
      <Footer />
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            backgroundColor: "rgba(25, 27, 40, 0.9)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255, 255, 255, 0.4)",
            borderRadius: "15px",
            color: "white",
          },
        }}
      >
        <DialogTitle>
          <VuiTypography color="white" fontWeight="bold">
            Confirmar Ação
          </VuiTypography>
        </DialogTitle>
        <DialogContent>
          <VuiTypography color="white">
            Deseja indicar esse lead como fraudador?
          </VuiTypography>
        </DialogContent>
        <DialogActions>
          <VuiButton onClick={handleCloseDialog} color="secondary">
            Não
          </VuiButton>
          <VuiButton onClick={handleConfirmFlag} color="info" autoFocus>
            Sim
          </VuiButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Leads;
